import { students } from '../data.js';

// Güvenli students getter
function getStudentsList() {
  return students || [];
}

/**
 * Doğum günü bildirimi için badge sayısını hesapla
 */
export function getBirthdayBadgeCount() {
  const todaysBirthdays = getTodaysBirthdays();
  const upcomingBirthdays = getUpcomingBirthdays();
  
  return todaysBirthdays.length + upcomingBirthdays.length;
}

/**
 * Bugünkü doğum günlerini getir
 * Yerel tarih kullanarak timezone sorunlarını önler
 */
export function getTodaysBirthdays() {
  const today = new Date();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();

  const studentsList = getStudentsList();
  return studentsList.filter(student => {
    if (!student.birthDate) return false;
    const [year, month, day] = student.birthDate.split('-').map(Number);
    return month === todayMonth && day === todayDay;
  });
}

/**
 * Doğum günü teması bilgilerini döndür
 */
export function getBirthdayTheme(student) {
  if (!student) return null;
  
  return {
    type: 'birthday',
    title: 'DOĞUM GÜNÜ',
    message: `Bugün ${student.name}'in doğum günü!`,
    studentName: student.name,
    avatar: student.avatar,
    icon: '🎂',
    colors: {
      primary: '#ff6b6b',
      secondary: '#ffd93d'
    }
  };
}

/**
 * Tema öncelik kontrolü - Doğum günü en üst öncelik
 */
export function checkBirthdayPriority() {
  const todaysBirthdays = getTodaysBirthdays();
  
  if (todaysBirthdays.length > 0) {
    return {
      hasBirthday: true,
      birthdayStudent: todaysBirthdays[0], // İlk öğrenciyi al
      theme: getBirthdayTheme(todaysBirthdays[0])
    };
  }
  
  return {
    hasBirthday: false,
    birthdayStudent: null,
    theme: null
  };
}

/**
 * Ana sayfa kartı için doğum günü mesajı
 */
export function getBirthdayCardMessage(student) {
  if (!student) return null;
  
  return `Bugün 4/P sınıfı kahramanlarından ${student.name}'in doğum günü, iyi ki doğdun!`;
}

/**
 * Yaklaşan doğum günlerini getir (7 gün içinde)
 */
export function getUpcomingBirthdays(days = 7) {
  const today = new Date();
  const upcoming = [];

  const studentsList = getStudentsList();
  studentsList.forEach(student => {
    if (!student.birthDate) return;

    const birthDate = new Date(student.birthDate);
    const thisYear = today.getFullYear();
    const nextBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
    
    if (nextBirthday < today) {
      nextBirthday.setFullYear(thisYear + 1);
    }

    const daysUntil = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil > 0 && daysUntil <= days) {
      upcoming.push({
        ...student,
        daysUntil,
        age: thisYear - birthDate.getFullYear(),
        nextBirthday
      });
    }
  });

  return upcoming.sort((a, b) => a.daysUntil - b.daysUntil);
}

/**
 * Öğretmen paneli için doğum günü bilgileri
 */
export function getBirthdayNotification() {
  const todaysBirthdays = getTodaysBirthdays();
  const upcomingBirthdays = getUpcomingBirthdays();
  
  return {
    today: {
      count: todaysBirthdays.length,
      students: todaysBirthdays.map(student => ({
        name: student.name,
        age: new Date().getFullYear() - new Date(student.birthDate).getFullYear()
      }))
    },
    upcoming: {
      count: upcomingBirthdays.length,
      students: upcomingBirthdays.map(student => ({
        name: student.name,
        daysUntil: student.daysUntil,
        age: student.age
      }))
    }
  };
}

/**
 * Tüm öğrencilerin doğum günlerini bugüne en yakın olandan sırala (sadece ay-gün bazlı)
 */
export function getAllBirthdaysSorted() {
  const today = new Date();
  const todayMonth = today.getMonth() + 1; // 1-12
  const todayDay = today.getDate(); // 1-31
  
  const studentsList = getStudentsList();
  return studentsList
    .filter(student => student.birthDate)
    .map(student => {
      const birthDate = new Date(student.birthDate);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      const birthMonth = birthDate.getMonth() + 1;
      const birthDay = birthDate.getDate();
      
      // Bugünden itibaren kaç gün sonra (sadece ay-gün bazlı)
      let daysUntil;
      
      if (birthMonth > todayMonth || (birthMonth === todayMonth && birthDay >= todayDay)) {
        // Bu yıl içinde
        const thisYearBirthday = new Date(today.getFullYear(), birthMonth - 1, birthDay);
        daysUntil = Math.ceil((thisYearBirthday - today) / (1000 * 60 * 60 * 24));
      } else {
        // Gelecek yıl
        const nextYearBirthday = new Date(today.getFullYear() + 1, birthMonth - 1, birthDay);
        daysUntil = Math.ceil((nextYearBirthday - today) / (1000 * 60 * 60 * 24));
      }
      
      return {
        ...student,
        age,
        daysUntil,
        monthDay: `${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`
      };
    })
    .sort((a, b) => a.daysUntil - b.daysUntil); // En yakın doğum günü önce
}

/**
 * Console'da test etmek için debug fonksiyonu
 */
export function testBirthdaySorting() {
  const sorted = getAllBirthdaysSorted();
  console.log('🎂 Doğum Günü Sıralaması (Bugüne En Yakın):', new Date().toLocaleDateString('tr-TR'));
  console.log('='.repeat(60));
  
  sorted.slice(0, 10).forEach((student, index) => {
    console.log(`${index + 1}. ${student.name}`);
    console.log(`   📅 ${student.monthDay} - ${student.age} yaşında`);
    console.log(`   ⏰ ${student.daysUntil} gün sonra`);
    console.log('---');
  });
  
  return sorted;
}

// Global fonksiyon olarak ekle
if (typeof window !== 'undefined') {
  window.testBirthdays = testBirthdaySorting;
}